import { FAIcon } from "../faIcon.js";
import { AppHelper } from "../../../appHelper.js";
import { ComponentHelper } from "../../util/componentHelper.js";
import { Helper } from "../../util/helper.js";
import { DatePicker, DatePickerProps } from "../datePicker.js";
import { CheckBox } from "../checkBox.js";
import EditSkillNumericTextBox from "./editSkillNumericTextBox.js";
import { Config } from "../../config/config.js";
import { Skill, TreeNodeVm } from "../../../generalSettings/skills/skillsInterfaces.js";
import React from "react";

export interface SkillEditBoxProps {
    skill?: Skill,
    treeNode?: TreeNodeVm,
    idx?: number,
    onChange?: (skill: Skill, value, field) => void,
    onUpdate?: () => void;
    RecordRight?: {
        CanRead: boolean;
        CanWrite: boolean;
        ResourceSkillsRight?: boolean;
        CanReadSkills: boolean;
        CanWriteSkills: boolean;
        CanChangeSkillprofile: boolean;
    };
    readOnly?: boolean;
    other?: { name: string, value: any }[];
    showDateAsCheckBox?: boolean;
    useTargetValues?: boolean;
    usePriority?: boolean;
}

interface SkillEditBoxState {
    focused: boolean;
    onHover: boolean;
    onHoverText: boolean;
    onHoverIcon: boolean;
    expandPath: boolean;
}

export class SkillEditBox extends React.Component<SkillEditBoxProps, SkillEditBoxState>{
    private divRef = null;
    common = new AppHelper();
    private factory = ComponentHelper.createFactory();
    private magicDelphiDBDateNull = new Date(1899, 11, 30);

    constructor(props: SkillEditBoxProps) {
        super(props);
        this.state = {
            focused: false,
            onHover: false,
            onHoverText: false,
            onHoverIcon: false,
            expandPath: false
        };
        this.setEvents();
    }

    private setEvents = () => {
        document.addEventListener('mousedown', this.handleClickOutside);
    }

    private createTextBox = (text: string) => {

        const textElem = (<div className={"truncate"} style={{ lineHeight: 1.2, whiteSpace: "wrap" }}>{text}</div>);

        return (
            <div title={text} onMouseOver={this.onHoverText} onMouseLeave={this.onHoverTextLeft} style={{ display: "flex", alignItems: "baseline", textOverflow: "ellipsis" }}>
                {textElem}
            </div>
        );
    }

    private createDateBox = (dateInt: number, field: string, withIcon: boolean = true) => {
        let iconName = "";
        let hoverText = "";
        let props: DatePickerProps = this.factory.getDefaultDatePickerProps();
        let value = this.getDate(dateInt);
        props.value = value;
        props.onChange = (field2: any, value: any) => {
            let newValue = Helper.dates.dateDiffInDays(this.magicDelphiDBDateNull, value);
            this.props.onChange(this.props.skill, newValue, field);
        }

        let diffDays = Helper.dates.dateDiffInDays(new Date(), value);
        if (diffDays < 0 || !Helper.hasValue(value)) { // abgelaufen
            iconName = "calendar-times";
            hoverText = Helper.getTranslation("Skills.HasElapsed");
        } else if (diffDays > 30) { // aktuell
            iconName = "calendar-check";
            hoverText = Helper.getTranslation("Skills.IsCurrent");
        } else { //läuft bald ab 
            iconName = "calendar-exclamation";
            hoverText = Helper.getTranslation("Skills.WillSoonElapsed");
        }
        let icon = withIcon ? (<div style={{ marginLeft: "10px", marginRight: "10px" }} title={hoverText}>
            <FAIcon name={iconName} attributes={["lg"]} />
        </div>) : null;
        let width = withIcon ? { width: "130px" } : {};
        return (
            <div style={{ display: "flex", alignItems: "center" }}>
                <div style={width}>
                    <DatePicker {...props} />
                </div>
                {icon}
            </div>
        );
    }

    private createCheckBox = (field: string) => {
        const updateValue = (date: Date = null) => {
            let newValue: number = null;
            if (Helper.hasValue(date)) {
                newValue = Helper.dates.dateDiffInDays(this.magicDelphiDBDateNull, date);
            }
            this.props.onChange(this.props.skill, newValue, field);
        }

        const tryUpdateByStorage = (): boolean => {
            let storage = Helper.loadDataFromStorage("maxEndDate");
            if (Helper.hasValue(storage)) {
                let value = Helper.dates.toDate(storage);
                updateValue(value);
                return true;
            } else {
                return false;
            }

        }
        let props = this.factory.getDefaultCheckBoxProps();
        props.value = Helper.getValueOrDefault(this.props.skill.Wert, 0) > 0
        props.disabled = this.props.readOnly;
        props.onChange = (field: any, value: boolean) => {
            if (value) {
                if (Helper.hasValue(this.props.other) && this.props.other.length > 0) {
                    let p = this.props.other.find(f => f.name === "maxEndDate");
                    if (p != null && p.value != null) {
                        updateValue(p.value as Date);
                    }
                } else if (!tryUpdateByStorage()) {
                    updateValue(new Date());
                }
            } else {
                updateValue();
            }
        }
        return (
            <div>
                <CheckBox {...props} />
            </div>
        )
    }

    private getDate = (days: number) => {
        if (Helper.hasValue(days) && days > 0) {
            return Helper.dates.addDays(this.magicDelphiDBDateNull, days);
        } else {
            return null;
        }
    }

    onHoverIcon = () => {
        this.setState({ onHoverIcon: true })
    }

    onHoverIconLeft = () => {
        this.setState({ onHoverIcon: false })
    }

    onHoverText = () => {
        this.setState({ onHoverText: true });
    }

    onHoverTextLeft = () => {
        this.setState({ onHoverText: false });
    }

    onHover = () => {
        this.setState({ onHover: true });
    }

    onHoverLeft = () => {
        this.setState({ onHover: false });
    }

    onFocus = () => {
        this.setState({ focused: true });
    }

    onFocusLeft = () => {
        this.setState({ focused: false });
    }

    setDivRef = (ref) => {
        this.divRef = ref;
    }

    handleClickOutside = (event) => {
        if (this.divRef && !this.divRef.contains(event.target)) {
            this.onFocusLeft();
        }
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside);
    }

    getCheckBox(field: string) {
        return <div style={{ marginBottom: "8px", alignItems: "center" }}>
            {this.createCheckBox(field)}
        </div>
    }

    getDatePicker(text: string, dateInt: number, field: string, withIcon: boolean = true) {
        return <div style={{ marginBottom: "8px", alignItems: "center" }}>
            {this.createDateBox(dateInt, field, withIcon)}
        </div>
    }

    private getActualElement = () => {
        let actualValue = <EditSkillNumericTextBox
            field="wert"
            maxValue={Config.settings.skills.maxSkillValue}
            minValue={0}
            value={this.props.skill.Wert}
            onChange={(value, field) => this.props.onChange(this.props.skill, value, field)}
            readOnly={this.props.readOnly}
            onFocus={this.onFocus}
            focused={this.state.focused}
            onHover={this.state.onHover} />
        return <div style={{ minHeight: "34px" }} onMouseLeave={this.onHoverLeft} onMouseOver={this.onHover} onBlur={this.onFocusLeft} ref={this.setDivRef}>
            {actualValue}
        </div>
    }

    private getTargetElement = () => {
        if (!this.props.useTargetValues)
            return null;
        let targetValue = <EditSkillNumericTextBox
            field="sollWert"
            maxValue={Config.settings.skills.maxSkillValue}
            minValue={0}
            value={this.props.skill.SollWert}
            onChange={(value, field) => this.props.onChange(this.props.skill, value, field)}
            readOnly={this.props.readOnly}
            onFocus={this.onFocus}
            focused={this.state.focused}
            onHover={this.state.onHover} />
        return <div onMouseLeave={this.onHoverLeft} onMouseOver={this.onHover} onBlur={this.onFocusLeft} >
            {targetValue}
        </div>
    }
    
    private getTargetDateElement = () => {
        if(!this.props.useTargetValues){
            return null;
        }
        let targetDateValue = this.getDatePicker("", this.props.skill.SollDate, "sollDate", false);
        return <div onMouseLeave={this.onHoverLeft} onMouseOver={this.onHover} onBlur={this.onFocusLeft} >
            {targetDateValue}
        </div>
    }

    private getPrioElement = () => {
        if (!this.props.usePriority)
            return null;
        let prioValue = <EditSkillNumericTextBox
            field="prio"
            maxValue={Config.settings.skills.maxPriorityValue}
            minValue={0}
            value={this.props.skill.Prio}
            onChange={(value, field) => this.props.onChange(this.props.skill, value, field)}
            readOnly={this.props.readOnly}
            onFocus={this.onFocus}
            focused={this.state.focused}
            onHover={this.state.onHover} />
        return <div onMouseLeave={this.onHoverLeft} onMouseOver={this.onHover} onBlur={this.onFocusLeft} >
            {prioValue}
        </div>
    }

    private toogleExpandPath = () => {
        var expandPath = this.state.expandPath;
        this.setState({ expandPath: !expandPath });
    }

    render() {
        let actualElement = <div></div>;
        let targetElement = <div></div>;
        let prioElement = <div></div>;
        let targetDateElement = <div></div>;
        if (this.props.skill.SkillType === 13) {
            actualElement = this.getActualElement();
            targetElement = this.getTargetElement();
            targetDateElement = this.getTargetDateElement();
            prioElement = this.getPrioElement();
        }
        else if (this.props.skill.SkillType === 19 && this.props.showDateAsCheckBox) {
            actualElement = this.getCheckBox("wert");
        }
        else if (this.props.skill.SkillType === 19) {
            actualElement = this.getDatePicker(this.props.skill.Rtext, this.props.skill.Wert, "wert");
        }
        return (
            <>
                <div style={{ paddingTop: "6px" }}>
                    {this.createTextBox(this.props.skill.Rtext)}
                    <div className={`text-ellipsis ${this.state.expandPath ? "text-ellipsis--expand" : ""}`} style={{
                        opacity: 0.5, fontSize: "0.8rem", lineHeight: "2", cursor: "pointer"
                    }} onClick={this.toogleExpandPath}>{this.props.treeNode.Path}</div>
                </div>
                {actualElement}
                {targetElement}
                {targetDateElement}
                {prioElement}
            </>
        );
    }
}